import { type FC, type MouseEvent, useMemo } from 'react';
import {
  SizedCell,
  SortableHeadCell,
  TextEllipsisWithTooltip,
} from '__components/Kizen/Table';
import { useTranslation } from 'react-i18next';
import ActionCell from '__components/Tables/ActionCell';
import SharingSettingsCell from '__components/AccessRequests/Tables/Cells/SharingSettingsCell';
import { CATALOGS_TABLE_ACTIONS } from 'pages/KnowledgeSettings/constants';
import type { KnowledgeCatalog } from '@kizen/api/knowledge';
import {
  getCatalogStatusConfig,
  getCatalogStatusKey,
  getPluginApiName,
  isPluginCatalog,
} from 'pages/KnowledgeSettings/helpers';
import { StyledStatus } from 'pages/KnowledgeSettings/styles';
import styled from '@emotion/styled';
import { PluginIconIndicator } from 'ts-components/Plugins/PluginIconIndicator';

const EMPTY_VALUE = '—';

const StyledSharingSettingsCell = styled.div`
  display: flex;
  align-items: center;
`;

const StyledBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background-color: var(--color-background-dark);
  color: var(--color-background-white);
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 3px;
  margin-left: 6px;
  text-transform: uppercase;
  vertical-align: middle;
`;

const NameCell: FC<{
  name: string;
  isBusinessDefault: boolean;
  isPlugin: boolean;
  pluginAppName: string;
  href: string;
  onClick: () => void;
}> = ({ name, isBusinessDefault, isPlugin, pluginAppName, href, onClick }) => {
  return (
    <>
      <TextEllipsisWithTooltip
        type="anchor"
        href={href}
        onClick={(e: MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          onClick();
        }}
      >
        {name}
        {isBusinessDefault ? <StyledBadge>Default</StyledBadge> : null}
      </TextEllipsisWithTooltip>
      {isPlugin ? <PluginIconIndicator appName={pluginAppName} /> : null}
    </>
  );
};

const ControlsCell: FC<{
  data?: KnowledgeCatalog;
  onSelectAction: (
    action: { value: keyof typeof CATALOGS_TABLE_ACTIONS },
    data: KnowledgeCatalog
  ) => void;
  canWrite: boolean;
}> = ({ data, onSelectAction, canWrite, ...props }) => {
  const { t } = useTranslation();
  const isBusinessDefault = data?.is_default ?? false;
  const isPlugin = isPluginCatalog(data);
  const options = useMemo(() => {
    if (!canWrite) return [];
    return [
      {
        label: t('View Catalog'),
        value: CATALOGS_TABLE_ACTIONS.view,
      },
      !isBusinessDefault &&
        (data?.is_enabled
          ? {
              label: t('Disable'),
              value: CATALOGS_TABLE_ACTIONS.disable,
            }
          : {
              label: t('Enable'),
              value: CATALOGS_TABLE_ACTIONS.enable,
            }),
      !isBusinessDefault &&
        !isPlugin && {
          label: t('Delete'),
          value: CATALOGS_TABLE_ACTIONS.delete,
        },
    ].filter(Boolean);
  }, [t, data?.is_enabled, isBusinessDefault, isPlugin, canWrite]);

  if (!canWrite) {
    return <SizedCell width="50px" padding="15px" />;
  }

  return (
    <ActionCell
      title={t('Select Action')}
      menuType="absolute"
      options={options}
      onSelectAction={onSelectAction}
      {...props}
      data={data}
      width="15px"
      padding="15px"
    />
  );
};

type UseColumnsProps = {
  onSelectAction: (
    action: { value: keyof typeof CATALOGS_TABLE_ACTIONS },
    data: KnowledgeCatalog
  ) => void;
  onClickName: (catalog: KnowledgeCatalog) => void;
  onClickFileSources: (catalog: KnowledgeCatalog) => void;
  onClickOnlineSources: (catalog: KnowledgeCatalog) => void;
  onClickSharingSettings: (catalog: KnowledgeCatalog) => void;
  canWrite: boolean;
  canViewSection: boolean;
};

export const useColumns = ({
  onSelectAction,
  onClickName,
  onClickFileSources,
  onClickOnlineSources,
  onClickSharingSettings,
  canWrite,
  canViewSection,
}: UseColumnsProps) => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        id: 'spacer-start',
        headCell: <SizedCell width="15px" />,
        cell: <SizedCell width="15px" />,
        format: () => <span />,
      },
      {
        id: 'name',
        headCell: (
          <SortableHeadCell label={t('Name')} width="175px" padding="30px" />
        ),
        cell: <SizedCell width="200px" padding="30px" />,
        format: (_: string, data: KnowledgeCatalog) => (
          <NameCell
            name={data.name}
            isBusinessDefault={data.is_default ?? false}
            isPlugin={isPluginCatalog(data)}
            pluginAppName={getPluginApiName(data)}
            href={`/knowledge/${data.id}`}
            onClick={() => onClickName(data)}
          />
        ),
      },
      {
        id: 'description',
        headCell: (
          <SortableHeadCell
            label={t('Description')}
            width="230px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="230px" padding="30px" />,
        format: (description: KnowledgeCatalog['description']) => (
          <TextEllipsisWithTooltip>
            {description || EMPTY_VALUE}
          </TextEllipsisWithTooltip>
        ),
      },
      {
        id: 'file_source_count',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('File Sources')}
            width="100px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="100px" padding="30px" />,
        format: (count: number, data: KnowledgeCatalog) => (
          <TextEllipsisWithTooltip
            type="anchor"
            href={`/knowledge/${data.id}/file-sources`}
            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              onClickFileSources(data);
            }}
          >
            {count ?? 0}
          </TextEllipsisWithTooltip>
        ),
      },
      {
        id: 'online_source_count',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Online Sources')}
            width="110px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="110px" padding="30px" />,
        format: (count: number, data: KnowledgeCatalog) => (
          <TextEllipsisWithTooltip
            type="anchor"
            href={`/knowledge/${data.id}/online-sources`}
            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              onClickOnlineSources(data);
            }}
          >
            {count ?? 0}
          </TextEllipsisWithTooltip>
        ),
      },
      {
        id: 'sharing_settings',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Sharing Settings')}
            width="180px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="180px" padding="30px" />,
        format: (_: unknown, data: KnowledgeCatalog) => (
          <StyledSharingSettingsCell>
            <SharingSettingsCell
              data={data}
              access={data.employee_access}
              canEdit={canViewSection && !data.is_default}
              forceEditable={!data.is_default}
              onClickEdit={() => onClickSharingSettings(data)}
              hideRoles={['Admin', 'Edit']}
            />
          </StyledSharingSettingsCell>
        ),
      },
      {
        id: 'is_enabled',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Status')}
            width="110px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="110px" padding="30px" />,
        format: (_: boolean, data: KnowledgeCatalog) => {
          const statusKey = getCatalogStatusKey(data);
          const config = getCatalogStatusConfig(t)[statusKey];
          return (
            <StyledStatus color={config?.color ?? null}>
              {config?.label ?? EMPTY_VALUE}
            </StyledStatus>
          );
        },
      },
      {
        id: 'actions',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Actions')}
            width="110px"
            padding="15px"
            align="right"
          />
        ),
        cell: (
          <ControlsCell onSelectAction={onSelectAction} canWrite={canWrite} />
        ),
      },
    ],
    [
      onSelectAction,
      onClickName,
      onClickFileSources,
      onClickOnlineSources,
      onClickSharingSettings,
      canWrite,
      canViewSection,
      t,
    ]
  );
};
